import { useAuth } from '@/hooks/useAuth';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2, Dice5, Search, TrendingUp, TrendingDown, Coins, Medal, Flame, Plus, Trophy, Trash2, CheckCircle2, Receipt } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import {
  admin_getCasinoStats, type CasinoPlayerStats,
  getSportsMatches, admin_createSportsMatch, admin_deleteSportsMatch, admin_settleSportsMatch,
  admin_getMatchBets, type MatchBet,
} from '@/lib/actions';
import type { SportsMatch } from '@/lib/types';
import { cn } from '@/lib/utils';

function netClass(n: number) { return (n ?? 0) >= 0 ? 'text-green-400' : 'text-red-400'; }
function fmt(n: number) { const v = n ?? 0; return `${v >= 0 ? '+' : ''}${v.toLocaleString('es-ES')}`; }
function num(n: number | undefined) { return n ?? 0; }

// ── Estadísticas ──
function StatsTab() {
  const [players, setPlayers] = useState<CasinoPlayerStats[]>([]);
  const [houseNet, setHouseNet] = useState(0);
  const [totalWagered, setTotalWagered] = useState(0);
  const [loadingData, setLoadingData] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<CasinoPlayerStats | null>(null);

  useEffect(() => {
    admin_getCasinoStats().then((res) => {
      if (res.success) { setPlayers(res.players); setHouseNet(res.houseNet); setTotalWagered(res.totalWagered); }
      setLoadingData(false);
    });
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? players.filter((p) => p.nickname.toLowerCase().includes(q)) : players;
  }, [players, search]);

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="rounded-2xl border border-white/8 bg-card/40 p-4"><p className="text-xs uppercase tracking-widest text-muted-foreground">Jugadores</p><p className="text-2xl font-black">{players.length}</p></div>
        <div className="rounded-2xl border border-white/8 bg-card/40 p-4"><p className="text-xs uppercase tracking-widest text-muted-foreground">Total apostado</p><p className="text-2xl font-black text-amber-400">{totalWagered.toLocaleString('es-ES')}</p></div>
        <div className="rounded-2xl border border-white/8 bg-card/40 p-4"><p className="text-xs uppercase tracking-widest text-muted-foreground">Balance de la casa</p><p className={cn('text-2xl font-black', netClass(houseNet))}>{fmt(houseNet)}</p></div>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar jugador..." className="pl-9" />
      </div>

      {loadingData ? (
        <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-white/8 py-16 text-muted-foreground"><Dice5 className="mb-3 h-10 w-10 opacity-40" /><p className="text-sm">Todavía no hay jugadas registradas.</p></div>
      ) : (
        <div className="space-y-2">
          {filtered.map((p, i) => {
            const rankColor = i === 0 ? 'text-amber-400' : i === 1 ? 'text-slate-300' : i === 2 ? 'text-amber-700' : 'text-muted-foreground';
            return (
              <button key={p.id} onClick={() => setSelected(p)} className="flex w-full items-center gap-3 rounded-2xl border border-white/8 bg-card/40 p-4 text-left transition-colors hover:border-primary/40">
                <div className="flex w-8 justify-center">{i < 3 && !search ? <Medal className={cn('h-5 w-5', rankColor)} /> : <span className="text-sm font-bold text-muted-foreground">{search ? '·' : i + 1}</span>}</div>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-semibold">{p.nickname}</p>
                  <p className="text-xs text-muted-foreground">{p.spins} jugada{p.spins !== 1 ? 's' : ''} · apostado {p.totalWagered.toLocaleString('es-ES')} · fichas {p.chips.toLocaleString('es-ES')}</p>
                </div>
                <div className="shrink-0 text-right">
                  <p className={cn('flex items-center justify-end gap-1 text-lg font-black', netClass(p.netTotal))}>{p.netTotal >= 0 ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}{fmt(p.netTotal)}</p>
                  <p className="text-[0.7rem] text-muted-foreground">balance total</p>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Detalle por usuario */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-md">
          {selected && (
            <>
              <DialogHeader><DialogTitle>{selected.nickname}</DialogTitle><DialogDescription>Balance de {selected.nickname} en JartiBet por sección.</DialogDescription></DialogHeader>
              <div className="space-y-3">
                <div className="flex items-center justify-between rounded-2xl border border-white/8 bg-card/40 p-4">
                  <span className="flex items-center gap-2 font-semibold"><Dice5 className="h-5 w-5 text-amber-400" /> Ruleta normal</span>
                  <span className={cn('text-lg font-black', netClass(selected.netRoulette))}>{fmt(selected.netRoulette)}</span>
                </div>
                <div className="flex items-center justify-between rounded-2xl border border-orange-500/20 bg-orange-500/5 p-4">
                  <span className="flex items-center gap-2 font-semibold"><Flame className="h-5 w-5 text-orange-400" /> Mega Fire</span>
                  <span className={cn('text-lg font-black', netClass(selected.netMegafire))}>{fmt(selected.netMegafire)}</span>
                </div>
                <div className="flex items-center justify-between rounded-2xl border border-sky-500/20 bg-sky-500/5 p-4">
                  <span className="flex items-center gap-2 font-semibold"><Trophy className="h-5 w-5 text-sky-400" /> Apuestas deportivas</span>
                  <span className={cn('text-lg font-black', netClass(selected.netBets))}>{fmt(selected.netBets)}</span>
                </div>
                <div className="flex items-center justify-between rounded-2xl border-2 border-amber-400/30 bg-amber-500/5 p-4">
                  <span className="font-black uppercase tracking-wide">Total JartiBet</span>
                  <span className={cn('text-2xl font-black', netClass(selected.netTotal))}>{fmt(selected.netTotal)}</span>
                </div>
                <p className="text-center text-xs text-muted-foreground">Fichas actuales: {selected.chips.toLocaleString('es-ES')}</p>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Gestión de apuestas deportivas ──
function CreateMatchDialog({ onCreated }: { onCreated: () => void }) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: '', date: '', team1: '', team2: '', oddsHome: '2.0', oddsDraw: '3.0', oddsAway: '2.5' });
  const [saving, setSaving] = useState(false);
  const set = (k: string, v: any) => setForm((p) => ({ ...p, [k]: v }));

  const create = async () => {
    if (!form.team1.trim() || !form.team2.trim()) { toast({ title: 'Faltan los equipos', variant: 'destructive' }); return; }
    setSaving(true);
    const res = await admin_createSportsMatch({
      title: form.title.trim() || `${form.team1} vs ${form.team2}`, date: form.date, team1: form.team1.trim(), team2: form.team2.trim(),
      oddsHome: Number(form.oddsHome), oddsDraw: Number(form.oddsDraw), oddsAway: Number(form.oddsAway),
    });
    if (res.success) { toast({ title: 'Partido creado', description: res.message }); setOpen(false); setForm({ title: '', date: '', team1: '', team2: '', oddsHome: '2.0', oddsDraw: '3.0', oddsAway: '2.5' }); onCreated(); }
    else toast({ title: 'Error', description: res.message, variant: 'destructive' });
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="gap-2"><Plus className="h-4 w-4" /> Crear partido</Button></DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Nuevo partido</DialogTitle><DialogDescription>Equipos, fecha/hora y cuotas (1 / X / 2).</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5"><Label>Título (opcional)</Label><Input value={form.title} onChange={(e) => set('title', e.target.value)} placeholder="Ej: Jornada 1 — Liga Cofrade" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Equipo 1 (local)</Label><Input value={form.team1} onChange={(e) => set('team1', e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Equipo 2 (visitante)</Label><Input value={form.team2} onChange={(e) => set('team2', e.target.value)} /></div>
          </div>
          <div className="space-y-1.5"><Label>Fecha y hora</Label><Input type="datetime-local" value={form.date} onChange={(e) => set('date', e.target.value)} /></div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5"><Label>Cuota 1</Label><Input type="number" step="0.01" value={form.oddsHome} onChange={(e) => set('oddsHome', e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Cuota X</Label><Input type="number" step="0.01" value={form.oddsDraw} onChange={(e) => set('oddsDraw', e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Cuota 2</Label><Input type="number" step="0.01" value={form.oddsAway} onChange={(e) => set('oddsAway', e.target.value)} /></div>
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button><Button onClick={create} disabled={saving}>{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Crear</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Apuestas de un partido: quién gana / pierde jartis ──
function MatchBetsDialog({ match, onClose }: { match: SportsMatch; onClose: () => void }) {
  const [bets, setBets] = useState<MatchBet[]>([]);
  const [summary, setSummary] = useState({ totalStake: 0, totalPaid: 0, playersNet: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    admin_getMatchBets(match.id).then((res) => {
      if (res.success) { setBets(res.bets); setSummary({ totalStake: res.totalStake, totalPaid: res.totalPaid, playersNet: res.playersNet }); }
      setLoading(false);
    });
  }, [match.id]);

  const selLabel = (b: MatchBet) => b.selection === 'home' ? b.team1 : b.selection === 'away' ? b.team2 : 'Empate';
  const statusChip = (b: MatchBet) =>
    b.status === 'won' ? 'bg-green-500/20 text-green-400' : b.status === 'lost' ? 'bg-rose-500/20 text-rose-400' : 'bg-sky-500/20 text-sky-400';
  const statusText = (b: MatchBet) => b.status === 'won' ? `Ganó +${(b.payout - b.stake).toLocaleString('es-ES')}` : b.status === 'lost' ? `Perdió -${b.stake.toLocaleString('es-ES')}` : 'Pendiente';

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="flex max-h-[90dvh] max-w-lg flex-col p-0">
        <DialogHeader className="px-6 pt-6">
          <DialogTitle>Apuestas · {match.team1} vs {match.team2}</DialogTitle>
          <DialogDescription>Quién ha apostado y cuántos jartis gana o pierde cada jugador.</DialogDescription>
        </DialogHeader>
        <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-6">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
          ) : bets.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-white/8 py-14 text-muted-foreground"><Receipt className="mb-3 h-9 w-9 opacity-40" /><p className="text-sm">Nadie ha apostado a este partido.</p></div>
          ) : (
            <>
              <div className="mb-4 grid grid-cols-3 gap-2 text-center">
                <div className="rounded-xl border border-white/8 bg-card/40 p-2"><p className="text-[0.65rem] uppercase text-muted-foreground">Apostado</p><p className="font-black text-amber-400">{summary.totalStake.toLocaleString('es-ES')}</p></div>
                <div className="rounded-xl border border-white/8 bg-card/40 p-2"><p className="text-[0.65rem] uppercase text-muted-foreground">Pagado</p><p className="font-black text-green-400">{summary.totalPaid.toLocaleString('es-ES')}</p></div>
                <div className="rounded-xl border border-white/8 bg-card/40 p-2"><p className="text-[0.65rem] uppercase text-muted-foreground">Balance jugadores</p><p className={cn('font-black', netClass(summary.playersNet))}>{fmt(summary.playersNet)}</p></div>
              </div>
              <div className="space-y-2">
                {bets.map((b) => (
                  <div key={b.id} className="flex items-center gap-3 rounded-2xl border border-white/8 bg-card/40 p-3">
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-semibold">{b.nickname}</p>
                      <p className="text-xs text-muted-foreground">{selLabel(b)} · cuota {b.odds} · {b.stake.toLocaleString('es-ES')} fichas</p>
                    </div>
                    <span className={cn('shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold', statusChip(b))}>{statusText(b)}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SportsTab() {
  const { toast } = useToast();
  const [matches, setMatches] = useState<SportsMatch[]>([]);
  const [betsMatch, setBetsMatch] = useState<SportsMatch | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  const fetchMatches = async () => { setLoading(true); const res = await getSportsMatches(); if (res.success) setMatches(res.matches); setLoading(false); };
  useEffect(() => { fetchMatches(); }, []);

  const settle = async (id: string, result: 'home' | 'draw' | 'away') => {
    setBusy(id);
    const res = await admin_settleSportsMatch(id, result);
    if (res.success) { toast({ title: 'Resuelto', description: res.message }); fetchMatches(); }
    else toast({ title: 'Error', description: res.message, variant: 'destructive' });
    setBusy(null);
  };
  const remove = async (id: string) => {
    const res = await admin_deleteSportsMatch(id);
    if (res.success) { toast({ title: 'Eliminado', description: res.message }); fetchMatches(); }
    else toast({ title: 'Error', description: res.message, variant: 'destructive' });
  };

  const resultLabel = (m: SportsMatch) => m.result === 'home' ? `Ganó ${m.team1}` : m.result === 'away' ? `Ganó ${m.team2}` : m.result === 'draw' ? 'Empate' : '';

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{matches.length} partido(s)</p>
        <CreateMatchDialog onCreated={fetchMatches} />
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
      ) : matches.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-white/8 py-16 text-muted-foreground"><Trophy className="mb-3 h-10 w-10 opacity-40" /><p className="text-sm">No hay partidos. Crea el primero.</p></div>
      ) : (
        <div className="space-y-3">
          {matches.map((m) => (
            <div key={m.id} className="rounded-2xl border border-white/8 bg-card/40 p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-bold">{m.team1} <span className="text-muted-foreground">vs</span> {m.team2}</p>
                  <p className="text-xs text-muted-foreground">{m.title}{m.date ? ` · ${new Date(m.date).toLocaleString('es-ES')}` : ''}</p>
                </div>
                <Badge className={m.status === 'settled' ? 'bg-green-500/20 text-green-400' : m.status === 'closed' ? 'bg-amber-500/20 text-amber-400' : 'bg-sky-500/20 text-sky-400'}>
                  {m.status === 'settled' ? `Resuelto · ${resultLabel(m)}` : m.status === 'closed' ? 'Cerrado' : 'Abierto'}
                </Badge>
              </div>
              <div className="mt-2 flex gap-2 text-xs text-muted-foreground">
                <span>1: <b className="text-foreground">{m.oddsHome}</b></span>
                <span>X: <b className="text-foreground">{m.oddsDraw}</b></span>
                <span>2: <b className="text-foreground">{m.oddsAway}</b></span>
              </div>
              {m.status !== 'settled' && (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <span className="text-xs font-semibold text-muted-foreground">Resolver:</span>
                  <Button size="sm" variant="outline" disabled={busy === m.id} onClick={() => settle(m.id, 'home')}><CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Gana {m.team1}</Button>
                  <Button size="sm" variant="outline" disabled={busy === m.id} onClick={() => settle(m.id, 'draw')}>Empate</Button>
                  <Button size="sm" variant="outline" disabled={busy === m.id} onClick={() => settle(m.id, 'away')}><CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Gana {m.team2}</Button>
                  {busy === m.id && <Loader2 className="h-4 w-4 animate-spin" />}
                </div>
              )}
              <div className="mt-3 flex items-center gap-2">
                <Button size="sm" variant="outline" className="gap-1" onClick={() => setBetsMatch(m)}><Receipt className="h-3.5 w-3.5" /> Ver apuestas</Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild><Button size="sm" variant="ghost" className="gap-1 text-destructive"><Trash2 className="h-3.5 w-3.5" /> Eliminar</Button></AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader><AlertDialogTitle>¿Eliminar este partido?</AlertDialogTitle><AlertDialogDescription>Se elimina el partido. Las apuestas asociadas quedarán sin liquidar si aún estaban pendientes.</AlertDialogDescription></AlertDialogHeader>
                    <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={() => remove(m.id)} className="bg-destructive hover:bg-destructive/80">Eliminar</AlertDialogAction></AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          ))}
        </div>
      )}

      {betsMatch && <MatchBetsDialog match={betsMatch} onClose={() => setBetsMatch(null)} />}
    </div>
  );
}

export default function AdminJartiBetPage() {
  const { userData, loading } = useAuth();
  const navigate = useNavigate();
  useEffect(() => { if (!loading && userData?.role !== 'admin') navigate('/jartinet', { replace: true }); }, [userData, loading, navigate]);
  if (loading || userData?.role !== 'admin') return <div className="flex h-[60vh] items-center justify-center"><Loader2 className="h-10 w-10 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6 pb-12">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-3xl border-2 border-amber-400/30 bg-gradient-to-br from-amber-950/50 via-black to-red-950/40 p-8">
        <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-amber-500/20 blur-3xl" />
        <div className="relative flex items-center gap-5">
          <div className="grid h-16 w-16 place-items-center rounded-2xl border border-amber-400/40 bg-amber-500/15 text-amber-300"><Dice5 className="h-9 w-9" /></div>
          <div>
            <h1 className="font-headline text-4xl font-bold md:text-5xl text-amber-200">JartiBet · Gestión</h1>
            <p className="mt-2 text-amber-100/70">Balance de los jugadores y gestión de apuestas deportivas.</p>
          </div>
        </div>
      </motion.div>

      <Tabs defaultValue="stats">
        <TabsList>
          <TabsTrigger value="stats">Estadísticas</TabsTrigger>
          <TabsTrigger value="sports">Apuestas</TabsTrigger>
        </TabsList>
        <TabsContent value="stats" className="mt-6"><StatsTab /></TabsContent>
        <TabsContent value="sports" className="mt-6"><SportsTab /></TabsContent>
      </Tabs>
    </div>
  );
}
